import { Skeleton } from '@material-ui/lab';
import { Stack, useSnackbarStack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useEffect, useRef, useState } from 'react';
import { trackEvent } from 'shared/helpers/Analytics';
import {
  InstantTransferConfirmLoadingState,
  isConfirmIllustrationState,
} from 'shared/modules/superpay/instant-transfer/InstantTransferConfirmLoadingState';
import { InstantTransferExpired } from 'shared/modules/superpay/instant-transfer/InstantTransferExpired';
import { InstantTransferFailed } from 'shared/modules/superpay/instant-transfer/InstantTransferFailed';
import { InstantTransferForm } from 'shared/modules/superpay/instant-transfer/InstantTransferForm';
import { InstantTransferPending } from 'shared/modules/superpay/instant-transfer/InstantTransferPending';
import { InstantTransferUnlockAccess } from 'shared/modules/superpay/instant-transfer/InstantTransferUnlockAccess';
import { InstantTransferSomethingWentWrong } from 'shared/modules/superpay/instant-transfer/InstantTransferSomethingWentWrong';
import { InstantTransferSuccess } from 'shared/modules/superpay/instant-transfer/InstantTransferSuccess';
import { InstantTransferUnavailable } from 'shared/modules/superpay/instant-transfer/InstantTransferUnavailable';
import {
  useConfirmInstantTransfer,
  useInstantTransferInformation,
} from 'shared/modules/superpay/SuperPayAPI';
import {
  SuperPayInstantTransferConfirmDTO,
  SuperPayInstantTransferDialogDTO,
} from 'shared/modules/superpay/SuperPayDTO';

export type Source = 'Load List' | 'Load Details' | 'Instant Transfer Offer';

function sendEvent({
  eventName,
  source,
  dialogInformation,
  dialogConfirmInformation,
}: {
  eventName: string;
  source?: Source;
  dialogInformation?: SuperPayInstantTransferDialogDTO;
  dialogConfirmInformation?: SuperPayInstantTransferConfirmDTO;
}) {
  trackEvent(eventName, {
    utm_medium: source,
    status: dialogConfirmInformation?.status || dialogInformation?.status,
    load_id: dialogConfirmInformation?.load_id || dialogInformation?.load_id,
    load_guid:
      dialogConfirmInformation?.load_guid || dialogInformation?.load_guid,
    instant_transfer_fee:
      dialogConfirmInformation?.fee_percentage ||
      dialogInformation?.fee_percentage,
    origin: dialogInformation?.origin,
    destination: dialogInformation?.destination,
  });
}

interface InstantTransferDialogContentProps {
  onClose: () => void;
  onSuccess?: () => void;
  loadGUID?: string;
  loadID?: string;
  source?: Source;
}

export function InstantTransferDialogContent({
  onClose,
  onSuccess,
  loadGUID,
  loadID,
  source,
}: InstantTransferDialogContentProps) {
  const isEnabled = !!(loadGUID || loadID);

  const [hasConfirmLoadingState, setConfirmLoadingState] =
    useState<boolean>(false);

  const hasOperationSucceededRef = useRef(false);

  const { addSnackbar } = useSnackbarStack();

  const {
    data: dialogInformation,
    isLoading,
    refetch: refetchDialogInformation,
  } = useInstantTransferInformation(
    { loadGUID, loadID },
    { enabled: isEnabled, refetchOnWindowFocus: false },
  );

  const {
    data: dialogConfirmInformation,
    mutateAsync: onConfirmInstantTransfer,
  } = useConfirmInstantTransfer(dialogInformation?.load_guid, {
    onSuccess: ({ data }) => {
      sendEvent({
        eventName: 'Carrier Outcome After Instant Transfer is Clicked',
        source,
        dialogInformation,
        dialogConfirmInformation: data,
      });

      const isIllustrationState = isConfirmIllustrationState(data.status);

      if (data.status === 'completed' || data.status === 'pending') {
        hasOperationSucceededRef.current = true;
      }

      if (!isIllustrationState) {
        setConfirmLoadingState(false);
        return;
      }

      setConfirmLoadingState(true);

      setTimeout(() => {
        setConfirmLoadingState(false);
      }, 2000);
    },
    onError: () => {
      setConfirmLoadingState(false);
      addSnackbar('Something went wrong. Reload or try again', {
        variant: 'error',
      });
    },
  });

  const closeDialog = () => {
    sendEvent({
      eventName: 'Carrier Closed Instant Transfer Dialog',
      source,
      dialogInformation,
      dialogConfirmInformation: dialogConfirmInformation?.data,
    });

    if (hasOperationSucceededRef.current) {
      onSuccess?.();
    }

    onClose();
  };

  useEffect(() => {
    if (dialogInformation) {
      sendEvent({
        eventName: 'Carrier Opened Instant Transfer Dialog',
        source,
        dialogInformation,
      });
    }
  }, [dialogInformation, source]);

  if (isLoading) {
    return (
      <Box padding="medium">
        <Stack>
          <Skeleton width="60%" height={16} />
          <Skeleton width="80%" height={16} />
          <Skeleton width="40%" height={16} />
          <Skeleton width="40%" height={16} />
          <Skeleton width="100%" height={16} />
          <Skeleton width="100%" height={16} />
          <Skeleton width="30%" height={16} />
        </Stack>
      </Box>
    );
  }

  if (hasConfirmLoadingState) {
    return (
      <InstantTransferConfirmLoadingState
        status={dialogConfirmInformation?.data.status || 'loading'}
      />
    );
  }

  if (!isEnabled || !dialogInformation) {
    return <InstantTransferSomethingWentWrong onClose={closeDialog} />;
  }

  if (
    dialogInformation?.settlement?.status === 'failed' ||
    dialogInformation?.settlement?.status === 'past_due'
  ) {
    return (
      <InstantTransferUnlockAccess
        onClose={closeDialog}
        settleInformation={dialogInformation.settlement}
        onSuccess={() => {
          hasOperationSucceededRef.current = true;
          void refetchDialogInformation();
        }}
      />
    );
  }

  if (dialogConfirmInformation?.data) {
    switch (dialogConfirmInformation.data.status) {
      case 'completed':
        return (
          <InstantTransferSuccess
            onClose={closeDialog}
            dialogConfirmInformation={dialogConfirmInformation.data}
          />
        );
      case 'pending':
        return (
          <InstantTransferPending
            onClose={closeDialog}
            dialogConfirmInformation={dialogConfirmInformation.data}
          />
        );
      case 'failed':
        return (
          <InstantTransferFailed
            onClose={closeDialog}
            dialogConfirmInformation={dialogConfirmInformation.data}
          />
        );
      case 'unavailable':
        return <InstantTransferUnavailable onClose={closeDialog} />;
      default:
        return <InstantTransferSomethingWentWrong onClose={closeDialog} />;
    }
  }

  switch (dialogInformation.status) {
    case 'expired':
      return (
        <InstantTransferExpired
          onClose={closeDialog}
          dialogInformation={dialogInformation}
        />
      );

    case 'available':
      return (
        <InstantTransferForm
          onClose={closeDialog}
          dialogInformation={dialogInformation}
          onConfirmInstantTransfer={() => {
            sendEvent({
              eventName: 'Carrier Clicks Get Paid Instantly',
              source,
              dialogInformation,
            });

            setConfirmLoadingState(true);
            setTimeout(() => {
              void onConfirmInstantTransfer();
            }, 2000);
          }}
        />
      );

    default:
      return <InstantTransferSomethingWentWrong onClose={closeDialog} />;
  }
}
